// THE PARTY'S STATEMENT AS A REGISTER — the same table Money movements and Trades draw (his words, 1 Nov 2026: "the party
// statement is not aligned with other statement+recent activity"; then "Amount, chain, date and time everything in 1st
// column- doesn't it look wrong"). One row per transaction, newest first, ONE FACT PER COLUMN:
//   When           the kit's When column (whenColumn: fmtStamp, mono, first, from 1280; below it the entry's second line)
//   Entry          the words only — what happened and with whom ("Received USDT", "Moved AED to Saffron Trade House"),
//                  the reference a quiet second line; no amount, no wallet (their own columns)
//   Account        the desk account each leg touched ("Hot · TRC20", "Office cash"), a line per leg — taken from the ledger's
//                  sentence ("… into <account>", "Paid … from <account>"): the statement route sends no account field
//   Movement       what it did to THEIR balance, signed, a line per asset; "—" on the brought-forward row (its figure is the
//                  opening, not a movement) and on a spot trade that netted to nothing (its words keep the quantities)
//   Balance after  their balance in that asset after it, in the box's words ("owes you 1,659 AED"), neutral
//   Type           the pill Money movements' Direction column draws; a trade's settle state beside it
// The phone card: the entry and its type, the movement and balance line, "account · when" as the meta. A row opens its
// entry where it stands (the screen decides how). The brought-forward row opens nothing.
import { Register, WhenInline, whenColumn, type RegisterColumn } from '../kit';
import { KindPill } from '../../Ledger';
import { boxParts } from '../../BoxLines';
import { displayAsset, fmtAmount, fmtStamp } from '../../../lib/format';
import { shortReference } from '../../../lib/naming';
import { MoneyText } from '../MoneyText';
import { Chevron } from '../movements/MovementRegister';
import { entryTag, isTrade, particulars, type JournalRow, type Leg } from './journal';

/** The registers' one time format (fmtStamp: "13:12" today, else the day and time) — the kit's When column says it. */
const when = (at: string | null): string => (at ? fmtStamp(at) : '');

function stateWord(e: Leg): string | null {
  if (e.state === 'reversal') return 'Reversal';
  return e.state === 'live' ? null : 'Reversed';
}

/** The ledger's sentence without the amounts the Movement seat already shows ("Bought USDT for AED @ 3.673", "Paid USDT from
 *  Hot · TRC20"): a figure goes only where an asset code follows it, so a rate stays (Astra, 1 Nov 2026: the same figure
 *  twice in one row). The whole sentence is the row's title and its sheet. */
export function withoutAmounts(words: string): string {
  return words.replace(/[+−-]?\d[\d,]*(?:\.\d+)? (?=[A-Z][A-Z0-9·]*\b)/g, '').replace(/\s{2,}/g, ' ').trim();
}

/** The sentence said from THIS party's page: their own name goes ("Moved AED to Saffron Trade House", never "Moved AED from
 *  Cedar International Trading FZCO t…" on Cedar's page), so the other side is what shows (Astra, 1 Nov 2026). */
export function fromHere(words: string, self?: string): string {
  if (!self) return words;
  const esc = self.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return words.replace(new RegExp(` (?:from|to|with|for) ${esc}(?=$|[,.;]| [a-z·—@(])`, 'g'), '').trim();
}

/** The desk account a money leg touched, from the ledger's sentence: the last "into <account>" ("Received … from Kestrel OTC
 *  into Bitget"), else the "from <account>" of a payment ("Paid … from Hot · TRC20"). A move between parties, the
 *  brought-forward row or a sentence that names no account: none. */
export function accountOf(l: Leg): string | null {
  // THE ROUTE NAMES IT (main 141a3bdd, ships 4 Nov 2026): rail_name per leg — null where no desk account moved.
  if (l.rail_name !== undefined) return l.rail_name;
  // FALLBACK for a Worker before 141a3bdd only — remove after the Wednesday 4 Nov 2026 release: the account read out of the
  // sentence. A trade's settled leg names its account too ("Received 500,000 INR into Angadiya cash · India"); "Bought …" none.
  if (l.kind.toLowerCase() === 'p2p' || l.txn_id === null) return null;
  return accountInWords(particulars(l.words).head);
}

/** The account a money sentence names, whatever kind the row was folded under. */
export function accountInWords(w: string): string | null {
  const into = w.lastIndexOf(' into ');
  if (into > 0) return w.slice(into + 6).trim() || null;
  if (/^(?:Paid|Sent)\b/.test(w)) { const from = w.lastIndexOf(' from '); if (from > 0) return w.slice(from + 6).trim() || null; }
  return null;
}

/** A spot trade whose legs net to nothing on their balance: its sentence keeps the quantities — they are the only place it
 *  says what was traded (Command Centre, 1 Nov 2026). */
const zeroNet = (r: JournalRow) => r.txn_id !== null && r.legs.every((l) => l.delta === 0);

/** The legs a row draws a line for — the same lines in Account, Movement and Balance after, so they align. */
const lines = (r: JournalRow): Leg[] => (r.txn_id === null || zeroNet(r) ? r.legs : r.legs.filter((l) => l.delta !== 0));

/** The entry's words: what happened and with whom — the amounts (Movement's), the account (Account's) and this party's own
 *  name taken out; a zero-net trade keeps its quantities. Two lines at most; the reference beneath. */
export function entryWords(r: JournalRow, self?: string): string {
  if (r.txn_id === null) return 'Balance brought forward';
  let w = particulars(r.lead.words).head;
  // The account the words name moves to the Account column, whichever way that column learned it.
  const acct = r.lead.kind.toLowerCase() === 'p2p' ? null : accountInWords(w);
  if (acct) w = w.slice(0, w.length - acct.length).replace(/ (?:into|from)\s*$/, '');
  if (!zeroNet(r)) w = withoutAmounts(w).replace(/ (?:at|@)\s*[\d.,]+$/, '').replace(/:\s*$/, '');   // the rate goes to Movement (rateOf)
  // A rate never strands from its "at" (Astra, 1 Nov 2026): "at 3.67" wraps as one.
  return fromHere(w, self).replace(/ (at|@) (?=\d)/g, '\u00a0$1\u00a0');
}

/** `inlineWhen`: the laptop table below 1280, where the When column is not drawn, says the time after the reference. */
export function EntryCell({ r, self, inlineWhen = false }: { r: JournalRow; self?: string; inlineWhen?: boolean }) {
  const e = r.lead;
  const ref = r.txn_id !== null && e.reference ? shortReference(e.reference) : '';
  return (
    <span className="block min-w-0" title={r.txn_id === null ? undefined : e.words}>
      <span className={`line-clamp-2 break-words font-medium ${e.state === 'live' ? '' : 'text-tg-hint'}`}><MoneyText text={entryWords(r, self)} /></span>
      {(ref || (inlineWhen && r.at)) && (
        <span className="mt-1 block truncate text-meta font-normal text-tg-hint">
          {ref && <span className="font-mono">{ref}</span>}{inlineWhen && <WhenInline at={r.at} sep={!!ref} />}
        </span>
      )}
    </span>
  );
}

export function AccountCell({ r }: { r: JournalRow }) {
  return <>{lines(r).map((l) => <div key={l.asset} className="truncate">{accountOf(l) ?? <span className="text-tg-hint">—</span>}</div>)}</>;
}

/** What the entry did to THEIR balance, signed (+ they owe more, − less), the unit muted after it; a line per asset. NEUTRAL
 *  INK: the registers' red is for money leaving the desk, and a receipt here is a minus on their balance — red would read
 *  as money going out (Astra, 1 Nov 2026). "—" where nothing moved: the brought-forward row, a zero-net trade. */
/** A trade's rate, read from its sentence ("… at 88.00", "… @ 3.673") — said once, under the movement, not inside the words
 *  (the audit, 1 Nov 2026: "Trade: due INR at 88.00"). A zero-net trade keeps it in its sentence. */
export function rateOf(r: JournalRow): string | null {
  if (r.txn_id === null || zeroNet(r) || !isTrade(r.lead)) return null;
  const m = / (?:at|@)\s*([\d.,]+)\s*$/.exec(particulars(r.lead.words).head);
  return m ? m[1]! : null;
}

export function MovementCell({ r }: { r: JournalRow }) {
  const rate = rateOf(r);
  return (
    <>
      {lines(r).map((l) => (
        <div key={l.asset} className="whitespace-nowrap">
          {r.txn_id === null || l.delta === 0 ? <span className="text-tg-hint">—</span> : (
            <><span className="amt">{l.delta > 0 ? '+' : '−'}{fmtAmount(Math.abs(l.delta), l.asset)}</span>{' '}
              <span className="text-tg-hint">{displayAsset(l.asset)}</span></>
          )}
        </div>
      ))}
      {rate && <div className="whitespace-nowrap text-meta text-tg-hint">at <span className="font-mono tabular">{rate}</span></div>}
    </>
  );
}

/** Their balance after it, per asset, in the box's words — neutral ink: it is a position, not money moving. Only for the
 *  row's OWN transaction: a money leg folded in by its deal (linked_deal_id) may be older than rows above it, and its
 *  balance then is not the balance after this row (Astra, 1 Nov 2026: the newest row disagreed with the box). */
export function BalanceCell({ r }: { r: JournalRow }) {
  return (
    <>
      {lines(r).map((l) => {
        if (r.txn_id !== null && l.txn_id !== r.txn_id) return <div key={l.asset} className="text-tg-hint">—</div>;
        const { words, figure } = boxParts(l.running, l.asset);
        return (
          <div key={l.asset} className="whitespace-nowrap">
            {figure
              ? <><span className="text-tg-hint">{words}</span> <span className="amt">{figure}</span> <span className="text-tg-hint">{displayAsset(l.asset)}</span></>
              : <span className="text-tg-hint">{displayAsset(l.asset)} square</span>}
          </div>
        );
      })}
    </>
  );
}

const SETTLE_WORD: Record<string, string> = { settled: 'Settled', awaiting: 'Settle later', absorbed: 'Carried in' };

/** A trade's settle state: the deal's own lifecycle when the page holds the deal; else a spot trade's ledger kind says it
 *  settled at booking ('trade'). */
function settleWord(r: JournalRow, dealLifecycle?: (id: string) => string | null | undefined): string | null {
  const t = r.legs.find((l) => isTrade(l) && l.txn_id !== null);
  if (!t) return null;
  const life = t.deal_id ? dealLifecycle?.(t.deal_id) : null;
  if (life) return SETTLE_WORD[life] ?? null;
  return t.kind.toLowerCase() === 'trade' ? 'Settled' : null;
}

export function TypeCell({ r, dealLifecycle }: { r: JournalRow; dealLifecycle?: (id: string) => string | null | undefined }) {
  const s = stateWord(r.lead) ?? settleWord(r, dealLifecycle);
  return (
    <span className="inline-flex flex-wrap items-center justify-end gap-2">
      {s && <span className={`text-meta ${r.lead.state === 'reversal' ? 'text-warn' : 'text-tg-hint'}`}>{s}</span>}
      <KindPill label={entryTag(r.lead)} />
    </span>
  );
}

export function StatementList({ rows, onOpen, label = 'Statement', self, dealLifecycle }: {
  rows: readonly JournalRow[];
  onOpen: (r: JournalRow) => void;
  label?: string;
  /** The party whose page this is, as the ledger's sentences name them. */
  self?: string;
  /** A deal's lifecycle when the page holds the deal (its settle state beside the Trade pill). */
  dealLifecycle?: (id: string) => string | null | undefined;
}) {
  const columns: RegisterColumn<JournalRow>[] = [
    whenColumn<JournalRow>((r) => r.at),
    { key: 'entry', head: 'Entry', cell: (r) => <EntryCell r={r} self={self} inlineWhen /> },
    // FIXED SEATS FOR THE FIGURES (Astra, 1 Nov 2026): percentages left a movement 73 px at 1024 and its "4,120,000 USDT" ran
    // into the next column. The money columns hold a nine-digit figure and its unit; the entry takes what is left, two lines.
    { key: 'acct', head: 'Account', width: 'w-[9rem]', cell: (r) => <AccountCell r={r} /> },
    { key: 'move', head: 'Movement', align: 'right', width: 'w-[10.5rem]', cell: (r) => <MovementCell r={r} /> },
    { key: 'bal', head: 'Balance after', align: 'right', width: 'w-[14rem]', cell: (r) => <BalanceCell r={r} /> },
    { key: 'type', head: 'Type', align: 'right', width: 'w-[9.5rem]', cell: (r) => <TypeCell r={r} dealLifecycle={dealLifecycle} /> },
    { key: 'go', head: '', width: 'w-10', cell: (r) => (r.txn_id === null ? null : <Chevron />) },
  ];
  return (
    <Register
      label={label}
      columns={columns}
      rows={rows}
      // The table from the width its seats need (kit tableFromColumns); cards below it.
      rowKey={(r) => r.key}
      onOpen={(r) => { if (r.txn_id !== null) onOpen(r); }}
      card={(r) => {
        const accounts = [...new Set(lines(r).map(accountOf).filter((a): a is string => !!a))].join(', ');
        return {
          title: (
            <>
              {/* the card's full width — see MovementRegister */}
              <span aria-hidden className="block h-0 w-screen max-w-full" />
              <span className="flex items-start justify-between gap-3">
                <span className="min-w-0"><EntryCell r={r} self={self} /></span>
                <TypeCell r={r} dealLifecycle={dealLifecycle} />
              </span>
              {/* ON THE PHONE, TWO LABELLED LINES (Astra, 1 Nov 2026: side by side, "+8,000,000 INR" and "owes you 407,900,000 INR"
                  ran into each other at 390): the label left and muted, the figures right. */}
              <span className="mt-3 grid max-w-md grid-cols-[auto_minmax(0,1fr)] items-start gap-x-3 gap-y-1 text-body font-normal">
                <span className="text-meta text-tg-hint">Movement</span>
                <span className="text-right" data-fig="move"><MovementCell r={r} /></span>
                <span className="text-meta text-tg-hint">Balance after</span>
                <span className="text-right" data-fig="bal"><BalanceCell r={r} /></span>
              </span>
            </>
          ),
          figure: r.txn_id === null ? undefined : <Chevron />,
          meta: accounts || undefined,   // the time is in the kit card's gutter (whenColumn)
        };
      }}
    />
  );
}
